import React, { useEffect, useState } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { useSelector } from "react-redux";
import countryToFlagCode from "../CountryFlag/CountryFlag";

const BubbleChart = ({
  selectedHeader,
  countryNames,
  destinationCountryData,
  oceanFreightData,
  currency,
  fobTotals,
}) => {
  const CIF = useSelector((state) => state.CIFValue.CIFValue);
  const FOB = useSelector((state) => state.FOBData.FOB);
  const EXW = useSelector((state) => state.EXWData.EXW);
  const [data, setData] = useState([{ title: "", x: 0, y: 0 }]);

  useEffect(() => {
    // const blueShades = ["#3498db", "#5dade2", "#85c1e9", "#aed6f1", "#d6eaf8"];

    const response = countryNames.map((countryName, index) => {
      const destinationMatch = destinationCountryData.find(
        (item) => item.country?.toLowerCase() === countryName?.toLowerCase()
      );

      const oceanMatch = oceanFreightData.find(
        (item) => item.country?.toLowerCase() === countryName?.toLowerCase()
      );

      const valueOfSum =
        destinationMatch?.tariff_calculate_on === "CIF"
          ? CIF[0] && CIF[0][countryName]
          : countryName && fobTotals[countryName];

      return {
        title: countryName,
        y:
          countryName?.toLowerCase() === selectedHeader.toLowerCase()
            ? 0
            : (valueOfSum * destinationMatch?.tariff) / 100 || 0,
        x:
          countryName?.toLowerCase() === selectedHeader.toLowerCase()
            ? 0
            : oceanMatch?.ocean_freight_road_freight
            ? Number(oceanMatch?.ocean_freight_road_freight)
            : 0 || 0,
        // color: blueShades[index % blueShades.length],
      };
    });

    response.length > 0 && setData(response);
  }, [countryNames, destinationCountryData, oceanFreightData, FOB, CIF]);

  useEffect(() => {
    let root = am5.Root.new("chartdivBubble");
    root.setThemes([am5themes_Animated.new(root)]);

    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: true,
        panY: true,
        wheelY: "zoomXY",
        start: 0,
        pinchZoomX: true,
        pinchZoomY: true,
      })
    );

    chart.zoomLevel = 2.3;
    chart.zoomOutButton.set("forceHidden", true);

    let xAxis = chart.xAxes.push(
      am5xy.ValueAxis.new(root, {
        renderer: am5xy.AxisRendererX.new(root, {
          strokeOpacity: 1,
          strokeWidth: 0.1,
        }),
        tooltip: am5.Tooltip.new(root, {}),
        min: 0,
        strictMinMax: true,
        extraMin: 0.5,
        extraMax: 0.5,
      })
    );

    xAxis.children.moveValue(
      am5.Label.new(root, {
        text: "Ocean Freight",
        fontFamily: "Inter",
        fontWeight: 500,
        fontSize: 14,
        fill: am5.color(0x2d4eb0),
        x: am5.p50,
        centerX: am5.p50,
      }),
      xAxis.children.length - 1
    );

    // xAxis.getNumberFormatter().set("numberFormat", `$#.00`);
    xAxis
      .getNumberFormatter()
      .set("numberFormat", `${currency.currency_symbol}#.00`);

    xAxis.get("renderer").grid.template.setAll({ visible: false });

    xAxis.get("renderer").labels.template.setAll({
      fontFamily: "Inter",
      fontSize: 14,
      fontWeight: 500,
      fill: am5.color(0x00000),
      rotation: 0,
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        maxDeviation: 0.1,
        start: 0.1,
        extraMin: 0.5,
        extraMax: 0.5,
        renderer: am5xy.AxisRendererY.new(root, {
          strokeOpacity: 1,
          strokeWidth: 0.2,
          fill: am5.color(0xadadad),
        }),
        tooltip: am5.Tooltip.new(root, {}),
        // numberFormat: "#'%'",
        // numberFormat: "#",
        numberFormat: `${currency.currency_symbol}#.0`,
        min: 0,
        strictMinMax: true,
      })
    );

    yAxis.children.moveValue(
      am5.Label.new(root, {
        rotation: -90,
        text: "Tariff Percentage",
        text: "Tariff Value",
        fontFamily: "Inter",
        fontSize: 14,
        fontWeight: 500,
        fill: am5.color(0x2d4eb0),
        y: am5.p50,
        centerX: am5.p50,
      }),
      0
    );

    yAxis.get("renderer").labels.template.set("adapter", {
      text: function (text, target) {
        return target.dataItem.value < 0 ? "" : text;
      },
    });

    yAxis.get("renderer").grid.template.set("adapter", {
      visible: function (visible, target) {
        return target.dataItem.value < 0 ? false : visible;
      },
    });

    yAxis.get("renderer").ticks.template.set("adapter", {
      visible: function (visible, target) {
        return target.dataItem.value < 0 ? false : visible;
      },
    });

    yAxis.get("renderer").labels.template.set("minHeight", 0);
    yAxis.get("renderer").grid.template.set("visible", false);

    yAxis.get("renderer").labels.template.setAll({
      fontFamily: "Inter",
      fontSize: 14,
      fontWeight: 500,
      fill: am5.color(0x00000),
      rotation: 0,
    });

    let series = chart.series.push(
      am5xy.LineSeries.new(root, {
        calculateAggregates: true,
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "y",
        valueXField: "x",
        valueField: "y",
        seriesTooltipTarget: "bullet",
        tooltip: am5.Tooltip.new(root, {
          pointerOrientation: "horizontal",
          // labelText:
          //   // "[bold][fontFamily:'Inter']{title}[/]\nTariff:[fontFamily:'Inter'] {valueYOriginal.formatNumber('#.0')}%\nOcean Freight[fontFamily:'Inter']: {valueXOriginal.formatNumber('#.0')}",
          //   `[bold][fontFamily:'Inter']{title}[/]\nTariff:[fontFamily:'Inter'] {valueYOriginal.formatNumber('#.0')}\nOcean Freight[fontFamily:'Inter']: {valueXOriginal.formatNumber('#.0')}`,
          labelText: `[bold][fontFamily:'Inter']{title}[/]
Tariff:[fontFamily:'Inter'] ${currency?.currency_symbol}{valueYOriginal.formatNumber('#.00')}
Ocean Freight[fontFamily:'Inter']: ${currency?.currency_symbol}{valueXOriginal.formatNumber('#.00')}`,
        }),
      })
    );

    series.strokes.template.set("visible", false);

    let circleTemplate = am5.Template.new({});
    circleTemplate.adapters.add("fill", function (fill, target) {
      let dataItem = target.dataItem;
      if (dataItem) {
        return am5.Color.fromString(dataItem.dataContext.color);
      }
      return fill;
    });

    series.bullets.push(function (root, series, dataItem) {
      const countryName = dataItem.dataContext.title;
      const countryCode = countryToFlagCode[countryName]?.toLowerCase() || "";

      const container = am5.Container.new(root, {
        centerX: am5.p50,
        centerY: am5.p50,
      });

      const backgroundBox = am5.Rectangle.new(root, {
        fill: am5.color(0xffffff),
        stroke: am5.color(0x2d4eb0),
        strokeWidth: 1,
        cornerRadius: 5,
        padding: 5,
      });

      container.children.push(backgroundBox);

      const circle = am5.Circle.new(root, {
        radius: 15,
        fill: am5.color(0xffffff),
        shadowColor: am5.color(0x000000),
        shadowBlur: 5,
        shadowOffsetX: 3,
        shadowOffsetY: 3,
      });

      // const flag = am5.Picture.new(root, {
      //   width: 30,
      //   height: 30,
      //   fontFamily: "Inter",
      //   src: `https://flagcdn.com/${countryCode}.svg`,
      //   centerX: am5.p50,
      //   centerY: am5.p50,
      //   scale: 1,
      // });

      // Create a container for the flag
      const flagContainer = root.container.children.push(
        am5.Container.new(root, {
          width: 40, // fixed container width
          height: 40, // fixed container height
          clipping: true, // ensures image stays inside container
          centerX: am5.p50,
          centerY: am5.p50,
        })
      );

      // Add the picture inside the container
      // const flag = flagContainer.children.push(
      //   am5.Picture.new(root, {
      //     src: `https://flagcdn.com/${countryCode}.svg`,
      //     width: 40, // same as container
      //     height: 40, // same as container
      //     scale: 1,
      //     centerX: am5.p50,
      //     centerY: am5.p50,
      //   })
      // );

      const flag =
        flagContainer &&
        flagContainer.children.push(
          am5.Picture.new(root, {
            src: `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`,
            width: 30,
            height: 30,
            centerX: am5.p50,
            centerY: am5.p50,
          })
        );

      let countryLabel = container.children.push(
        am5.Label.new(root, {
          text: countryName,
          paddingLeft: 5,
          populateText: true,
          fontWeight: "bold",
          fontSize: 13,
          centerY: am5.p50,
        })
      );

      circle.on("radius", function (radius) {
        countryLabel.set("x", radius);
      });

      container.children.push(circle);
      container.children.push(flag);
      container.set("mask", circle);

      return am5.Bullet.new(root, {
        sprite: container,
        dynamic: true,
      });
    });

    const distanceThreshold = 1;

    series.bullets.push(function (root, series, dataItem) {
      const index = series.dataItems.indexOf(dataItem);
      let centerX = am5.p50;
      let dx = 0;

      if (index > 0) {
        const previousDataItem = series.dataItems[index - 1];
        const currentX = dataItem.dataContext?.x || 0;
        const previousX = previousDataItem.dataContext?.x || 0;

        if (Math.abs(currentX - previousX) < distanceThreshold) {
          return null;
        }
      }

      return am5.Bullet.new(root, {
        sprite: am5.Label.new(root, {
          text: dataItem.dataContext.title,
          fontSize: 12,
          fontFamily: "Inter",
          fill: am5.color(0x333333),
          centerX: centerX,
          dx: dx,
          centerY: am5.p100,
          dy: -10,
          background: am5.RoundedRectangle.new(root, {
            fill: am5.color(0xccccff),
            fillOpacity: 0.5,
            cornerRadius: 8,
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: 5,
            paddingRight: 5,
          }),
        }),
        dynamic: true,
      });
    });

    series.set("heatRules", [
      {
        target: circleTemplate,
        min: 3,
        max: 20,
        dataField: "value",
        key: "radius",
      },
    ]);

    // ⚠️ Modified Here: Handle base value logic
    const baseX = 0.0;
    const baseY = 0.0;

    const processedData = data.map((item) => ({
      ...item,
      valueXOriginal: item.x,
      valueYOriginal: item.y,
      x: item.x === 0 ? baseX : item.x,
      y: item.y === 0 ? baseY : item.y,
    }));

    series.data.setAll(processedData);
    // Calculate min and max of X and Y
    const xValues = processedData.map((d) => d.x);
    const yValues = processedData.map((d) => d.y);

    const xMin = Math.min(...xValues);
    const xMax = Math.max(...xValues);
    const yMin = Math.min(...yValues);
    const yMax = Math.max(...yValues);

    // Add 10% padding around the data
    const xPadding = (xMax - xMin) * 0.1 || 0.1; // fallback padding for identical min/max
    const yPadding = (yMax - yMin) * 0.1 || 0.1;

    // Zoom the axis to fit the data with padding
    xAxis.zoomToValues(xMin - xPadding, xMax + xPadding);
    yAxis.zoomToValues(yMin - yPadding, yMax + yPadding);

    chart.set(
      "cursor",
      am5xy.XYCursor.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        snapToSeries: [series],
      })
    );

    series.appear(1000);
    chart.appear(1000, 100);
    root._logo.dispose();

    return () => {
      root.dispose();
    };
  }, [data]);

  return (
    <div id="chartdivBubble" style={{ width: "100%", height: "343px" }}></div>
  );
};

export default BubbleChart;
